// National Romanian rules, as data with a source on every line.
//
// NONE of these has been checked against the official text yet. They are
// written from memory as the starting point, and every one says so
// (`verified: false`) until someone reads it in the Monitorul Oficial and
// flips it. The compliance report shows the flag next to the rule, because a
// building that "complies" with a misremembered number is worse than one
// that shows the number is unconfirmed.
import type { EdgeKind, UnitType } from "../domain/rules.ts";

export interface Source {
  act: string;
  article?: string;
  verified: boolean;
  note?: string;
}

/** Minimum usable area per dwelling by number of rooms. */
export const MIN_DWELLING_AREA: Record<UnitType, number> = {
  "1": 37,
  "2": 58,
  "3": 77,
  "4": 90,
  "5": 105,
};
export const MIN_DWELLING_AREA_SOURCE: Source = {
  act: "Legea 114/1996 a locuinței",
  article: "Anexa 1",
  verified: false,
  note: "Valori din memorie. De verificat dacă tabelul se aplică oricărei locuințe noi sau doar unor categorii.",
};

/**
 * Floor under any setback towards a neighbour where the facade has windows:
 * the Civil Code forbids a direct view closer than 2 m to the boundary. The
 * street edge has no such floor; a PUZ may put the building on the alignment.
 */
export const MIN_SETBACK: Record<EdgeKind, number> = {
  aliniament: 0,
  lateral: 2,
  posterior: 2,
};
export const MIN_SETBACK_SOURCE: Source = {
  act: "Codul civil",
  article: "art. 615 (vederea directă la 2 m)",
  verified: false,
  note: "Se aplică fațadelor cu ferestre; un perete orb poate sta la 60 cm (art. 612) sau pe limită, cu acordul vecinului.",
};

/** A building above this cornice height is a high building, with its own fire rules. */
export const HIGH_BUILDING_HEIGHT = 28;
export const HIGH_BUILDING_SOURCE: Source = {
  act: "Normativ P118/1999 (securitate la incendiu)",
  verified: false,
  note: "Peste 28 m: clădire înaltă (scări, desfumare, acces pompieri). Peste 45 m: foarte înaltă.",
};

/** Longest walk from a flat's front door to a stair, metres (escape route inside the building). */
export const MAX_DISTANCE_TO_STAIR = 25;
export const ESCAPE_SOURCE: Source = {
  act: "Normativ P118/1999 (securitate la incendiu)",
  verified: false,
  note: "Măsurată în linie dreaptă de la fiecare ușă de intrare la cea mai apropiată scară, deci mai scurtă decât drumul pe coridor. Limita diferă după tipul clădirii și după una sau două direcții de evacuare: de verificat în P118.",
};

/** Lift required above this many storeys above ground. */
export const LIFT_REQUIRED_ABOVE_STOREYS = 5; // more than P+4E
export const LIFT_SOURCE: Source = {
  act: "Legea 114/1996",
  article: "Anexa 1",
  verified: false,
};

export const SUNLIGHT_SOURCE: Source = {
  act: "Ordinul MS 119/2014",
  article: "art. 3",
  verified: false,
  note: "1½ h de soare direct la solstițiul de iarnă în camera de zi.",
};

/** Spaces for people with disabilities, as a share of the total, and at least one. */
export const ACCESSIBLE_SHARE = 0.04;
export const ACCESSIBLE_SOURCE: Source = {
  act: "Normativ NP 051-2012 (accesibilitate)", verified: false,
  note: "Procent din memorie; îl stabilesc norma și regulamentul local. Desenate ca locurile cele mai apropiate de nuclee.",
};
/** Spaces fitted or ducted for charging electric cars, as a share of the total. */
export const EV_SHARE = 0.2;
export const EV_SOURCE: Source = {
  act: "Legea 372/2005 (performanța energetică), cu modificările ulterioare", verified: false,
  note: "Cerința pentru clădirile rezidențiale noi cu parcare vine din directiva UE privind clădirile; procentul e provizoriu până la verificare.",
};

/** Used only when neither CU nor PUZ states a parking norm. */
export const DEFAULT_PARKING_PER_DWELLING = 1;
export const PARKING_SOURCE: Source = {
  act: "HG 525/1996 (RGU)",
  article: "Anexa 5",
  verified: false,
  note: "Norma locală (de ex. HCGMB în București) o înlocuiește de obicei. Ia-o din CU / PUZ.",
};

// ---------- retail parks ----------

/**
 * Customer parking for shops, by the size of the whole scheme: one space per
 * this many m² of built area (Adc). Written from memory of HG 525/1996,
 * Annex 5, point 5.3: up to 400 m², 1 / 200 m²; 400–600 m², 1 / 100 m²;
 * 600–2,000 m², 1 / 50 m²; over 2,000 m², 1 / 40 m². Staff parking comes on
 * top and the annex does not size it.
 */
export const RETAIL_PARKING_STEPS: { upTo: number; m2PerSpace: number }[] = [
  { upTo: 400, m2PerSpace: 200 },
  { upTo: 600, m2PerSpace: 100 },
  { upTo: 2000, m2PerSpace: 50 },
  { upTo: Infinity, m2PerSpace: 40 },
];
export const RETAIL_PARKING_SOURCE: Source = {
  act: "HG 525/1996 (RGU)", article: "Anexa 5, pct. 5.3 (comerț)", verified: false,
  note: "Treptele sunt din memorie. Regulamentul local (de ex. HCGMB 66/2006 în București) și PUZ-ul le înlocuiesc de obicei; la ele se adaugă locurile pentru personal.",
};

/**
 * Free paved depth a lorry needs in front of a loading dock it reverses
 * into at right angles: an articulated lorry (16.5 m), a rigid lorry
 * (10–12 m), a van. Design-guide figures, not a law.
 */
export const DOCK_APRON = { articulated: 30, rigid: 18, van: 10 } as const;
export const DOCK_APRON_SOURCE: Source = {
  act: "Practică de proiectare (ghiduri pentru zone de livrare)", verified: false,
  note: "Cheu perpendicular, manevră cu o singură întoarcere. Un cheu în unghi sau o curte cu sens unic cer mai puțin; un TIR care și întoarce în curte cere un cerc de ~25 m.",
};

/** Longest straight walk from a parking space to a shop entrance before shoppers stop using the space. */
export const MAX_WALK_TO_ENTRANCE = 150;
export const WALK_SOURCE: Source = {
  act: "Practică de planificare comercială", verified: false,
  note: "Locurile de dincolo de ~150 m se umplu ultimele și doar în zilele de vârf; distanța e în linie dreaptă, deci mai scurtă decât drumul real.",
};

/** Longest straight-line distance from any point of a shop to an exit, metres. */
export const MAX_EXIT_DISTANCE_RETAIL = 40;
export const EXIT_RETAIL_SOURCE: Source = {
  act: "Normativ P118/1999 (securitate la incendiu)", verified: false,
  note: "Limita depinde de gradul de rezistență la foc, de sprinklere și de numărul de direcții de evacuare; ieșirile socotite sunt intrarea din față și ușile din spate.",
};

/** A fire engine reaches every unit's facade along a road at least this wide. */
export const FIRE_ROAD_WIDTH = 3.5;
export const FIRE_ACCESS_SOURCE: Source = {
  act: "Normativ P118/1999 (securitate la incendiu)", verified: false,
  note: "Drum carosabil pentru autospeciale la cel puțin o fațadă a fiecărei unități; în față aleea de circulație, în spate curtea de livrare.",
};

/** Non-residential buildings: spaces with a charging point. */
export const EV_SHARE_NONRES = 0.2;
export const EV_NONRES_SOURCE: Source = {
  act: "Directiva (UE) 2024/1275 privind performanța energetică a clădirilor", article: "art. 14", verified: false,
  note: "Din memorie: clădiri nerezidențiale noi cu peste 5 locuri, un punct de încărcare la 5 locuri și precablare pentru jumătate. Transpunerea în legea română e de verificat.",
};
